import axios from "axios";
import { Controllers, StorageUploadPresignRequest } from "../types/api.types";
import { validateStorageUploadPresignRequest } from "../utils/validation";
import api from "./api";

interface FileExistsResponse {
  exists: boolean;
}

const axiosApi = axios.create({});

export const storageApi = {
  /**
   * Generate a pre-signed URL for file upload
   * @param request Upload request parameters
   */
  async getUploadUrl(request: StorageUploadPresignRequest): Promise<string> {
    const valid = await validateStorageUploadPresignRequest(null, request);
    if (valid !== "valid") {
      throw new Error(valid);
    }
    const response = await api.post<{ data: string }>(
      `/${Controllers.Storage}/generate-upload-url`,
      request
    );
    return response.data.data;
  },

  /**
   * Generate a pre-signed URL for file download.
   *
   * StorageController exposes this as a POST taking a SingleParamRequest, whose
   * single field is named `id`, and answers with `data: { url }`. This used to
   * issue a GET with the key as a query parameter and read `data` as a string,
   * so every call failed with "Request method 'GET' is not supported". It went
   * unnoticed because nothing called it until the device edit form started
   * resolving its photo keys through here.
   *
   * The expiry is fixed at 10 minutes server-side, so a caller-supplied value
   * would be silently ignored - the parameter is gone rather than lying.
   */
  async getDownloadUrl(key: string): Promise<string> {
    const response = await api.post<{ data: { url: string } }>(
      `/${Controllers.Storage}/generate-download-url`,
      { id: key }
    );
    return response.data.data.url;
  },

  /**
   * Delete a file from storage
   * @param key File path/key in storage
   */
  async deleteFile(key: string): Promise<void> {
    await api.delete(`/${Controllers.Storage}/${key}`);
  },

  /**
   * Check if a file exists in storage
   * @param key File path/key in storage
   */
  async checkFileExists(key: string): Promise<boolean> {
    const response = await api.get<{ data: FileExistsResponse }>(
      `/${Controllers.Storage}/${key}/exists`
    );
    return response.data.data.exists;
  },

  /**
   * Upload a file using a pre-signed URL
   * @param url Pre-signed URL for upload
   * @param file File to upload
   * @param contentType File content type
   */
  async uploadFile(url: string, file: File): Promise<void> {
    try {
      await axiosApi.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  },

  /**
   * Helper function to upload a file with automatic URL generation
   * @param key File path/key in storage
   * @param file File to upload
   */
  async uploadFileWithUrl(key: string, file: File): Promise<string> {
    if (!!key && !!file) {
      const url = await this.getUploadUrl({
        key,
        contentType: file.type,
        contentLength: file.size,
        duration: 10,
      });
      await this.uploadFile(url, file);
    }
    return key;
  },

  /**
   * Upload APK file to storage
   * @param file APK file to upload
   * @param fileType Type of APK (CLIENT or INSTALLER or ADMIN)
   * @param fileName Optional custom file name
   * @param onProgress Optional progress callback
   */
  async uploadApk(
    file: File, 
    fileType: string, 
    version: string,
    versionCode: number,
    fileName?: string,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);
    formData.append('version', version);
    formData.append('versionCode', versionCode.toString());
    if (fileName) {
      formData.append('fileName', fileName);
    }
    const response = await api.post<{ data: string }>(
      `/${Controllers.Storage}/upload-apps`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    
    if (!response.data || !response.data.data) {
      throw new Error('Upload failed: No response data received');
    }
    
    return response.data.data;
  },
};
